import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from 'lib/httpClient';
import { ApiResponse } from 'further-types/api';
import { useEffect } from 'react';
import { useNotification } from 'hooks/ui/useNotification';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';

async function fetchAdviser({ queryKey }) {
  const [, id] = queryKey;

  const {
    data: { data },
  } = await api.get(`adviser/${id}`);
  return data;
}

async function createAdviser(adviserData) {
  const { data: response } = await api.post(`adviser/create`, adviserData);
  return response;
}

async function updateAdviser(id, adviserData) {
  const { data: response } = await api.put(`adviser/update/${id}`, adviserData);
  return response;
}

function useAdviserCreateAPI() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAdviser,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['advisers'] }),
        queryClient.invalidateQueries({ queryKey: ['organisations'] }),
      ]),
  });
}

function useAdviserFetchAPI({ params: { id } }) {
  return useQuery({
    queryKey: ['adviser', id],
    queryFn: fetchAdviser,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: !!id,
  });
}

function useAdviserUpdateAPI({ params: { id } }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateAdviser(id, data),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['adviser', id] }),
        queryClient.invalidateQueries({ queryKey: ['advisers'] }),
        queryClient.invalidateQueries({ queryKey: ['organisations'] }),
      ]),
  });
}

export function useUpdateAdviserFeeAmendments() {
  const queryClient = useQueryClient();
  const handleApiException = useApiExceptionHandler();
  return useMutation({
    mutationFn: async (requestBody: {
      id: string;
      changeToInitialInvestmentFees: Array<unknown>;
      commissionOnInitialInvestmentFees: Array<unknown>;
    }) => {
      const { data } = await api.put<ApiResponse>(
        `adviser/update-fee-amendments/${requestBody.id}`,
        requestBody,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['adviser', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['advisers'] });
    },
    onError: (error) => {
      handleApiException(error);
    },
  });
}

export function useDeleteAdviser() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const handleApiException = useApiExceptionHandler();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<ApiResponse>(`adviser/${id}`);
      return data;
    },
    onSuccess: (data) => {
      notification.success(data?.responseMsg ?? 'Adviser deleted.');
      queryClient.invalidateQueries({ queryKey: ['advisers'] });
    },
    onError: (error) => {
      handleApiException(error);
    },
  });
}

export function useAdviser(params) {
  const notification = useNotification();
  const create = useAdviserCreateAPI();
  const update = useAdviserUpdateAPI(params);

  useEffect(() => {
    const errorMessage =
      //@ts-expect-error
      create.error?.response?.data?.responseMsg ??
      //@ts-expect-error
      update.error?.response?.data?.responseMsg;

    if (errorMessage) {
      notification.error(errorMessage, undefined);
    }
  }, [
    //@ts-expect-error
    create.error?.response?.data?.responseMsg,
    //@ts-expect-error
    update.error?.response?.data?.responseMsg,
    notification,
  ]);

  useEffect(() => {
    const successMessage = create.data?.responseMsg ?? update.data?.responseMsg;

    if (successMessage) {
      notification.success(successMessage, undefined);
    }
  }, [create.data?.responseMsg, update.data?.responseMsg, notification]);

  return {
    create,
    update,
    fetch: useAdviserFetchAPI(params),
  };
}
